<template>
  <div class="notification" :class="`${notificationClass}`" @click="closeNotification">
    <div class="notification-icon">
      <i :class="`flaticon-${notificationClass}`"></i>
    </div>
    <div class="notification-body">
      <div class="notification-title" v-if="title">{{ $tr(title) }}</div>
      <div class="notification-message">{{ $tr(text) }}</div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";

export default {
  name: "Notification",
  props: {
    status: {
      // status => -1: error, 0: info, 1:success, 2: warning
      type: Number,
      default: 0,
    },
    title: {
      type: String,
      default: "Notification Title",
    },
    message: {
      type: String,
      default: "Notification Message",
    },
  },
  emits: ["close"],
  computed: {
    ...mapState({
      stateCommon: "common",
    }),
    /**
     * The message as text. Some API answers carry markup (<div>, <br>, one line per store of a
     * sync): each becomes a line break here instead of showing its tags.
     */
    text() {
      return String(this.message == null ? "" : this.message)
        .replace(/<br\s*\/?>|<\/(div|p|li)>/gi, "\n")
        .replace(/<[^>]+>/g, "")
        .replace(/\n{2,}/g, "\n")
        .trim();
    },
    notificationClass() {
      if (this.status === -1) return "error";
      if (this.status === 1) return "success";
      if (this.status === 2) return "warning";
      return "info";
    },
  },
  methods: {
    closeNotification() {
      this.$emit("close");
    },
  },
};
</script>

<style lang="css" scoped>
.notification {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  background: var(--notification-bg);
  border-radius: 14px;
  padding: 14px 16px;
  border-left: 4px solid var(--notification-border);
  box-shadow: var(--shadow-lg);
  width: 300px;
  cursor: pointer;
  z-index: var(--z-notification, 70);
}

/* A notice appears in place; it no longer slides in or lifts on hover (owner, 2026-09-21: no motion). */
.notification:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
}

.notification.error   { border-left-color: var(--status-error); }
.notification.success { border-left-color: var(--status-success); }
.notification.warning { border-left-color: var(--status-warning); }
.notification.info    { border-left-color: var(--status-info); }

.notification-icon {
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  flex-shrink: 0;
}

.notification.error   .notification-icon { background: var(--danger-bg, #fee2e2); color: var(--status-error); }
.notification.success .notification-icon { background: var(--success-bg, #d1fae5); color: var(--status-success); }
.notification.warning .notification-icon { background: var(--warning-bg, #fef3c7); color: var(--status-warning); }
.notification.info    .notification-icon { background: var(--info-bg, #dbeafe); color: var(--status-info); }

.notification-body {
  flex: 1;
  min-width: 0;
}

.notification-title {
  font-size: 0.75rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  margin-bottom: 0.2rem;
}

.notification.error   .notification-title { color: var(--status-error); }
.notification.success .notification-title { color: var(--status-success); }
.notification.warning .notification-title { color: var(--status-warning); }
.notification.info    .notification-title { color: var(--status-info); }

.notification-message {
  color: var(--notification-message);
  font-size: 0.8rem;
  line-height: 1.4;
  white-space: pre-line;
}
</style>
